import * as React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, useMediaQuery, useTheme } from '@mui/material';

import { NavList, TopBorderPaper } from 'components';
import { ROUTES } from 'consts';
import { EUserRole } from 'enums';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { arrayIntersect } from 'utils';

interface ProfileNavigatorProps {}

export const ProfileNavigator: React.FunctionComponent<ProfileNavigatorProps> = () => {
  const theme = useTheme();
  const up_xl = useMediaQuery(theme.breakpoints.up('xl'));
  const down_md = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { userRoles, orgSlug } = useAppSelector(selectCurrentUserInfo);

  const profileNavItems = [
    {
      id: 'user-settings',
      label: 'User Settings',
      link: ROUTES.PROFILE_USER_SETTINGS,
      roles: [EUserRole.BASIC, EUserRole.ADMIN],
    },
    {
      id: 'company-settings',
      label: 'Company Settings',
      link: ROUTES.COMPANY_SETTINGS,
      roles: [EUserRole.ADMIN],
    },
    {
      id: 'zone-categories',
      label: 'Zone Categories',
      link: ROUTES.ZONE_CATEGORIES,
      roles: [EUserRole.ADMIN],
    },
    {
      id: 'notifications',
      label: 'Notifications',
      link: ROUTES.PROFILE_NOTIFICATIONS,
      roles: [EUserRole.BASIC, EUserRole.ADMIN],
    },
    {
      id: 'password',
      label: 'Password',
      link: ROUTES.PROFILE_PASSWORD,
      roles: [EUserRole.BASIC, EUserRole.ADMIN],
    },
    {
      id: 'invite-users',
      label: 'Invite Users',
      link: ROUTES.PROFILE_INVITE_USERS,
      roles: [EUserRole.ADMIN],
    },
    {
      id: 'billing',
      label: 'Billing',
      link: ROUTES.PROFILE_BILLING,
      roles: [EUserRole.ADMIN],
    },
  ];

  const navItems = profileNavItems.filter((menu) => {
    return menu.roles.length === 0 || arrayIntersect(userRoles as any[], menu.roles, 'name').length !== 0;
  });

  const activeLink = location.pathname.replace(`/${orgSlug}/profile/`, '');

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 480, paddingX: 3, paddingY: 6 }}>
      <Box
        sx={{
          width: '100%',
          position: 'relative',
          maxWidth: up_xl ? theme.breakpoints.values.sm : 'unset',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {!down_md && (
          <Box
            sx={
              up_xl ? { position: 'absolute', left: -200, top: 96 } : { minWidth: 170, marginRight: 3, paddingTop: 12 }
            }
          >
            <NavList navItems={navItems} direction="column" activeLink={activeLink} />
          </Box>
        )}

        <TopBorderPaper
          sx={{
            paddingX: { xs: 3, sm: 12, md: 3, lg: 12 },
            paddingY: 8,
            maxWidth: theme.breakpoints.values.sm,
            width: '100%',
          }}
        >
          <Outlet />
        </TopBorderPaper>
      </Box>
    </Box>
  );
};
